/**
 * Programmatic tool calling (PTC) — let Claude call our READ tools from inside
 * the code-execution container instead of through the context window.
 *
 * Normally every tool call is a round trip: Claude calls the tool, the whole
 * result lands in the prompt, Claude reasons, calls the next one. Under PTC
 * Claude writes a script; when that script calls a tool the container pauses,
 * the call executes as usual, and the result returns **to the running code** —
 * not to Claude's context. Only the script's final output enters the prompt, so
 * token cost scales with the ANSWER rather than with everything read to reach
 * it. That is the whole point for paginated walks and fat search results.
 *
 * Requires the `code_execution_20260120` server tool to be present in the same
 * request (see ./code-execution). `allowed_callers` naming a tool that is not in
 * `tools` is a malformed request, so this is a no-op whenever code execution is
 * not attached.
 *
 * WHY BOTH CALLERS: we set `['direct', 'code_execution_20260120']`, not just the
 * container. Passing only the container REMOVES Claude's ability to call the
 * tool normally — that is the documented default for `web_search_20260209`, and
 * it would force a script for even a single-record lookup, adding container
 * latency to the cheapest possible call. Listing both leaves the direct path
 * intact and makes PTC an option Claude can take when it pays.
 *
 * READ-ONLY, ALWAYS. A tool invoked from inside a script does not pass through
 * the HITL approval flow — the container calls it directly. Marking a write tool
 * PTC-eligible would therefore let Claude perform an unapproved mutation by
 * wrapping it in Python, silently bypassing the approval gate the tool exists
 * behind. The curated allow-list below is reads only, and `assertReadOnly`
 * re-checks every entry against mutating verb prefixes so a careless future
 * addition fails loudly instead of quietly opening that hole.
 *
 * NOT COMPATIBLE WITH: `strict: true`, `disableParallelToolUse`, a forced
 * `toolChoice`, or MCP tools. Meridian's chat path uses none of those. The MCP
 * incompatibility is why PTC cannot help the Hub MCP server's token cost — that
 * is a separate problem with a separate fix.
 */

import { logCapabilitySkip, supportsProgrammaticToolCalling } from '../../models/capabilities';
import { logger } from '../../logger';

/** The code-execution tool version Claude calls our tools from. */
export const PTC_CALLER = 'code_execution_20260120' as const;

/** Callers allowed for a PTC-eligible tool: keep the direct path, add the container. */
export const PTC_ALLOWED_CALLERS: readonly ['direct', typeof PTC_CALLER] = [
  'direct',
  PTC_CALLER,
];

/**
 * Feature flag, independent of `MERIDIAN_CODE_EXECUTION` so PTC can be rolled
 * back without giving up code execution itself (they fail in different ways:
 * code execution is additive, PTC changes how existing tools are invoked).
 */
export function isProgrammaticToolCallingEnabled(): boolean {
  return process.env.MERIDIAN_PROGRAMMATIC_TOOL_CALLING === 'true';
}

/**
 * Verb prefixes that mean a tool mutates something. A PTC-eligible tool must
 * never start with one of these — see the READ-ONLY note above. Deliberately
 * broader than strictly necessary: a false positive here costs one tool its PTC
 * eligibility, a false negative costs an ungated write.
 */
const MUTATING_VERB_PREFIXES: readonly string[] = [
  'add_',
  'apply_',
  'approve_',
  'archive_',
  'assign_',
  'attribute_',
  'bail_',
  'block_',
  'bulk_',
  'cancel_',
  'copy_',
  'create_',
  'delete_',
  'download_',
  'draft_',
  'edit_',
  'enroll_',
  'enrich_',
  'export_',
  'forget_',
  'generate_',
  'grant_',
  'link_',
  'log_',
  'manage_',
  'mark_',
  'merge_',
  'move_',
  'pause_',
  'promote_',
  'publish_',
  'record_',
  'remember_',
  'remove_',
  'rename_',
  'reorder_',
  'resend_',
  'resume_',
  'schedule_',
  'send_',
  'set_',
  'snooze_',
  'unblock_',
  'update_',
  'use_',
  'void_',
  'withdraw_',
];

/**
 * Curated PTC allow-list: read tools whose results are large, paginated, or
 * repeatedly walked — i.e. where "filter inside the container" actually pays.
 * Ordered by measured byte cost on the Meridian surfaces.
 *
 * Adding to this list is a security-relevant change, not a tuning knob. Reads
 * only; `assertReadOnly` enforces it.
 */
export const PTC_ELIGIBLE_TOOLS: ReadonlySet<string> = new Set([
  // Top tool-byte consumers on slack + hub-chat.
  'search_crm',
  'find_company',
  'find_contact',
  'search_communications',
  'list_communications',
  // Paginated walks — the case PTC exists for: the cursor loop runs in the
  // container and only the filtered answer reaches the prompt.
  'list_emails',
  'search_emails',
  'list_deals',
  'search_deals',
  'list_tasks',
  'search_tasks',
  'list_tickets',
  'search_ticket',
  'list_contacts',
  'list_companies',
]);

/**
 * Throw if any allow-listed name looks like a mutation. Runs once at module
 * load so a bad addition breaks the build/boot rather than production.
 */
function assertReadOnly(names: Iterable<string>): void {
  const offenders = [...names].filter((name) =>
    MUTATING_VERB_PREFIXES.some((prefix) => name.startsWith(prefix)),
  );
  if (offenders.length > 0) {
    throw new Error(
      `PTC_ELIGIBLE_TOOLS contains apparent mutations: ${offenders.join(', ')}. ` +
        'A tool called from inside code execution bypasses HITL approval — reads only.',
    );
  }
}
assertReadOnly(PTC_ELIGIBLE_TOOLS);

interface ApplyPtcOptions {
  /** True only when the code-execution server tool is attached to this request. */
  readonly codeExecutionAttached: boolean;
  /**
   * The model this request will run on. PTC is a MODEL capability, not a
   * provider one: Claude Haiku 4.5 runs the sandbox but rejects the whole
   * request when any tool carries `allowed_callers` (see
   * `../../models/capabilities`). Omitted/unknown → treated as unsupported.
   */
  readonly modelId?: string | null;
  readonly logContext?: Record<string, unknown>;
}

/**
 * Return a NEW tool map with `allowedCallers` set on the PTC-eligible tools.
 *
 * No-op — returns the SAME reference — when the flag is off, when code execution
 * is not attached, or when no eligible tool is present. Existing
 * `providerOptions.anthropic` values (notably `deferLoading` from tool search)
 * are merged, never overwritten: a deferred tool stays deferred and simply
 * becomes container-callable once discovered.
 */
export function applyProgrammaticToolCalling(
  tools: Record<string, unknown>,
  options: ApplyPtcOptions,
): Record<string, unknown> {
  if (!isProgrammaticToolCallingEnabled()) return tools;
  if (!options.codeExecutionAttached) {
    // Flag on but no container to call from. Not an error — code execution is
    // itself flag- and provider-gated — but silence here would look like PTC was
    // active when it never applied.
    logger.warn('programmatic tool calling enabled but code execution is not attached', {
      ...options.logContext,
      op: 'applyProgrammaticToolCalling',
    });
    return tools;
  }

  if (!supportsProgrammaticToolCalling(options.modelId)) {
    // THE production failure this guard exists for: Haiku 4.5 accepts the
    // sandbox but 400s the entire request if any tool names the container as a
    // caller — no assistant message, no partial answer, just a dead turn. Code
    // execution itself stays attached; only the container-callable marking is
    // dropped, so the model keeps the sandbox and calls tools the direct way.
    logCapabilitySkip('programmatic_tool_calling', options.modelId, options.logContext);
    return tools;
  }

  const eligible = Object.keys(tools).filter((name) => PTC_ELIGIBLE_TOOLS.has(name));
  if (eligible.length === 0) return tools;

  const result: Record<string, unknown> = { ...tools };
  for (const name of eligible) {
    const existing = (
      tools[name] && typeof tools[name] === 'object' ? tools[name] : {}
    ) as {
      providerOptions?: { anthropic?: Record<string, unknown> } & Record<string, unknown>;
    };
    const existingProviderOptions = existing.providerOptions ?? {};
    const existingAnthropic = existingProviderOptions.anthropic ?? {};

    result[name] = {
      ...(tools[name] as Record<string, unknown>),
      providerOptions: {
        ...existingProviderOptions,
        anthropic: {
          ...existingAnthropic,
          allowedCallers: [...PTC_ALLOWED_CALLERS],
        },
      },
    };
  }

  logger.info('programmatic tool calling applied', {
    ...options.logContext,
    op: 'applyProgrammaticToolCalling',
    eligibleToolCount: eligible.length,
    eligibleTools: eligible,
  });

  return result;
}
